/**
 * PeToTa Business — Day Close MOCK API (B1.10).
 *
 * Mock implementation of the 5-state machine wire-level contract from
 * ADR-110, used by Storybook / Lovable previews and the dev environment
 * before the backend lands. The live HTTP variant is in
 * `day-close-api-http.ts`; the mock↔live dispatcher is `day-close-api.ts`.
 *
 * Simulates:
 *   start → patchCashCounts → patchNonCashCounts → patchDeposit → submit
 *                                                               → reopen
 *
 * Variance is computed on the fly (counted − expected); tolerance policy
 * comes from `seeds.ts` (Lovable 3-field shape: cashMinor / perTenderMinor
 * / hardBlockMinor). Idempotency is simulated via an in-memory submit cache.
 *
 * @see docs/decisions/business/ADR-110-business-dayclose-reconciliation.md
 * @see day-close-api-http.ts (live wire)
 * @see day-close-api.ts      (dispatcher)
 */

import {
  SEED_DAY_CLOSE_SNAPSHOT,
  SEED_DAY_CLOSE_SNAPSHOT_PREREQ_BLOCKED,
} from './seeds';
import {
  TRY_DENOMINATIONS,
  type DayCloseSnapshot,
  type DenominationCountEntry,
  type NonCashTenderCount,
  type PatchCashCountsRequest,
  type PatchDepositRequest,
  type PatchNonCashCountsRequest,
  type ReopenDayCloseRequest,
  type StartDayCloseRequest,
  type SubmitDayCloseRequest,
  type TryDenominationCode,
  type VarianceRow,
  type VarianceTolerancePolicy,
  type ZReportPayload,
} from './types';

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/** Per-session draft store (replaces backend `business_day_close_sessions`). */
const sessionStore = new Map<string, DayCloseSnapshot>();

/** Idempotency cache: `sessionId:idempotencyKey` → cached submit result. */
const idempotencyCache = new Map<string, DayCloseSnapshot>();

function cloneSnapshot(s: DayCloseSnapshot): DayCloseSnapshot {
  return JSON.parse(JSON.stringify(s)) as DayCloseSnapshot;
}

function totalFromDenominations(
  entries: readonly DenominationCountEntry[],
): number {
  return entries.reduce((sum, e) => {
    const denom = TRY_DENOMINATIONS.find((d) => d.code === e.code);
    if (!denom) return sum;
    return sum + denom.unitMinor * e.count;
  }, 0);
}

function computeVarianceRows(
  snapshot: DayCloseSnapshot,
  tolerance: VarianceTolerancePolicy,
): { rows: VarianceRow[]; totalAbsMinor: number; allWithinTolerance: boolean } {
  const rows: VarianceRow[] = [];

  const cashVar = snapshot.cash.countedCashMinor - snapshot.cash.expectedCashMinor;
  rows.push({
    method: 'cash',
    expectedMinor: snapshot.cash.expectedCashMinor,
    countedMinor: snapshot.cash.countedCashMinor,
    varianceMinor: cashVar,
    withinTolerance: Math.abs(cashVar) <= tolerance.cashMinor,
  });

  for (const t of snapshot.nonCashTenders) {
    const v = t.countedMinor - t.expectedMinor;
    rows.push({
      method: t.method,
      expectedMinor: t.expectedMinor,
      countedMinor: t.countedMinor,
      varianceMinor: v,
      withinTolerance: Math.abs(v) <= tolerance.perTenderMinor,
    });
  }

  const totalAbsMinor = rows.reduce((acc, r) => acc + Math.abs(r.varianceMinor), 0);
  const allWithinTolerance = rows.every((r) => r.withinTolerance);

  return { rows, totalAbsMinor, allWithinTolerance };
}

function applyVariance(snapshot: DayCloseSnapshot): DayCloseSnapshot {
  const { rows, totalAbsMinor, allWithinTolerance } = computeVarianceRows(
    snapshot,
    snapshot.tolerance,
  );

  const requiresOwnerOverride = rows.some(
    (r) => Math.abs(r.varianceMinor) >= snapshot.tolerance.hardBlockMinor,
  );
  const requiresManagerOverride =
    !requiresOwnerOverride && !allWithinTolerance;

  return {
    ...snapshot,
    variance: {
      rows,
      totalAbsMinor,
      withinTolerance: allWithinTolerance,
      requiresManagerOverride,
      requiresOwnerOverride,
    },
  };
}

/** Derived Z-report builder — flat Lovable shape. */
function buildZReport(snapshot: DayCloseSnapshot): ZReportPayload {
  const grossMinor =
    snapshot.cash.expectedCashMinor +
    snapshot.nonCashTenders.reduce((s, t) => s + t.expectedMinor, 0);
  const taxMinor = Math.round((grossMinor * 0.18) / 1.18);
  const netMinor = grossMinor - taxMinor;

  return {
    branchName: snapshot.branchName,
    businessDate: snapshot.businessDate,
    closedAt: new Date().toISOString(),
    closedByName: snapshot.openedByName,
    grossMinor,
    discountMinor: 3500,
    taxMinor,
    netMinor,
    refundMinor: 0,
    voidedMinor: 0,
    tenders: [
      {
        method: 'cash',
        amountMinor: snapshot.cash.countedCashMinor,
        count: 9,
      },
      ...snapshot.nonCashTenders.map((t) => ({
        method: t.method,
        amountMinor: t.countedMinor,
        count: t.transactionCount,
      })),
    ],
    categories: [
      { key: 'grooming', label: 'Bakım', amountMinor: 350000 },
      { key: 'boarding', label: 'Pansiyon', amountMinor: 85000 },
      { key: 'shop', label: 'Mağaza', amountMinor: 128500 },
      { key: 'training', label: 'Eğitim', amountMinor: 40000 },
    ],
    denominations: snapshot.cash.denominationCounts,
    override: snapshot.override.present
      ? {
          managerName: snapshot.override.managerName,
          reasonCode: snapshot.override.reasonCode,
          explanation: snapshot.override.explanation,
        }
      : null,
  };
}

export async function startDayClose(
  req: StartDayCloseRequest,
): Promise<DayCloseSnapshot> {
  await sleep(180);
  const seed =
    req.branchId === 'DEMO_PREREQ_BLOCKED'
      ? SEED_DAY_CLOSE_SNAPSHOT_PREREQ_BLOCKED
      : SEED_DAY_CLOSE_SNAPSHOT;
  const snapshot = cloneSnapshot(seed);
  sessionStore.set(snapshot.sessionId, snapshot);
  return snapshot;
}

export async function getDayCloseSnapshot(
  sessionId: string,
): Promise<DayCloseSnapshot> {
  await sleep(120);
  const existing = sessionStore.get(sessionId);
  if (!existing) {
    const snapshot = cloneSnapshot(SEED_DAY_CLOSE_SNAPSHOT);
    sessionStore.set(snapshot.sessionId, snapshot);
    return snapshot;
  }
  return existing;
}

export async function patchCashCounts(
  req: PatchCashCountsRequest,
): Promise<DayCloseSnapshot> {
  await sleep(140);
  const existing = sessionStore.get(req.sessionId);
  if (!existing) throw new Error('Day-close session not found');

  const counted = totalFromDenominations(req.counts);
  const next: DayCloseSnapshot = {
    ...existing,
    state: 'counting',
    currentStep: 'cash',
    cash: {
      ...existing.cash,
      countedCashMinor: counted,
      denominationCounts: req.counts,
    },
  };
  const withVariance = applyVariance(next);
  sessionStore.set(req.sessionId, withVariance);
  return withVariance;
}

export async function patchNonCashCounts(
  req: PatchNonCashCountsRequest,
): Promise<DayCloseSnapshot> {
  await sleep(140);
  const existing = sessionStore.get(req.sessionId);
  if (!existing) throw new Error('Day-close session not found');

  const mergedTenders: NonCashTenderCount[] = existing.nonCashTenders.map((t) => {
    const patch = req.tenders.find((x) => x.method === t.method);
    return patch
      ? {
          ...t,
          countedMinor: patch.countedMinor,
          referenceSlip: patch.referenceSlip,
        }
      : t;
  });

  const next: DayCloseSnapshot = {
    ...existing,
    state: 'reconciling',
    currentStep: 'non_cash',
    nonCashTenders: mergedTenders,
  };
  const withVariance = applyVariance(next);
  sessionStore.set(req.sessionId, withVariance);
  return withVariance;
}

export async function patchDeposit(
  req: PatchDepositRequest,
): Promise<DayCloseSnapshot> {
  await sleep(120);
  const existing = sessionStore.get(req.sessionId);
  if (!existing) throw new Error('Day-close session not found');

  const next: DayCloseSnapshot = {
    ...existing,
    state: 'review',
    currentStep: 'review',
    deposit: {
      closingFloatMinor: req.closingFloatMinor,
      depositAmountMinor: req.depositAmountMinor,
    },
  };
  sessionStore.set(req.sessionId, next);
  return next;
}

export async function submitDayClose(
  req: SubmitDayCloseRequest,
): Promise<DayCloseSnapshot> {
  await sleep(420);
  const cacheKey = `${req.sessionId}:${req.idempotencyKey}`;
  const cached = idempotencyCache.get(cacheKey);
  if (cached) return cached;

  const existing = sessionStore.get(req.sessionId);
  if (!existing) throw new Error('Day-close session not found');

  const withVariance = applyVariance(existing);
  if (withVariance.variance.requiresManagerOverride && !req.override) {
    throw Object.assign(new Error('Manager override required'), {
      code: 'MANAGER_OVERRIDE_REQUIRED',
    });
  }
  if (withVariance.variance.requiresOwnerOverride && !req.override) {
    throw Object.assign(new Error('Owner override required'), {
      code: 'OWNER_OVERRIDE_REQUIRED',
    });
  }
  if (req.override && req.override.managerPin !== '1234') {
    throw Object.assign(new Error('Invalid manager PIN'), {
      code: 'INVALID_MANAGER_PIN',
    });
  }
  if (
    req.override &&
    req.override.reasonCode === 'other_explained' &&
    (!req.override.explanation || req.override.explanation.trim().length < 10)
  ) {
    throw Object.assign(new Error('Explanation required (min 10 chars)'), {
      code: 'OVERRIDE_EXPLANATION_REQUIRED',
    });
  }

  const now = new Date().toISOString();
  const overridePresent = Boolean(req.override);
  const next: DayCloseSnapshot = {
    ...withVariance,
    state: 'closed',
    currentStep: 'submit',
    submittedAt: now,
    closedAt: now,
    override: overridePresent
      ? {
          present: true,
          managerName: 'Cenk Yılmaz',
          reasonCode: req.override!.reasonCode,
          explanation: req.override!.explanation,
        }
      : withVariance.override,
    zReportPayload: null,
  };
  const withZ: DayCloseSnapshot = {
    ...next,
    zReportPayload: buildZReport(next),
  };
  sessionStore.set(req.sessionId, withZ);
  idempotencyCache.set(cacheKey, withZ);
  return withZ;
}

export async function reopenDayClose(
  req: ReopenDayCloseRequest,
): Promise<DayCloseSnapshot> {
  await sleep(240);
  const existing = sessionStore.get(req.sessionId);
  if (!existing) throw new Error('Day-close session not found');
  if (existing.state !== 'closed') {
    throw Object.assign(new Error('Can only reopen a closed day'), {
      code: 'INVALID_STATE',
    });
  }
  if (!req.reason || req.reason.trim().length < 4) {
    throw Object.assign(new Error('Reopen reason required'), {
      code: 'REOPEN_REASON_REQUIRED',
    });
  }
  const next: DayCloseSnapshot = {
    ...existing,
    state: 'counting',
    currentStep: 'cash',
    version: existing.version + 1,
    submittedAt: null,
    closedAt: null,
    zReportPayload: null,
  };
  sessionStore.set(req.sessionId, next);
  return next;
}

/**
 * Helper: generate idempotency-key per ADR-022 / ADR-110 contract.
 * Format: `branchId:businessDate:attemptNo`.
 */
export function buildIdempotencyKey(
  branchId: string,
  businessDate: string,
  attemptNo: number,
): string {
  return `${branchId}:${businessDate}:${attemptNo}`;
}

export const _internal = {
  computeVarianceRows,
  applyVariance,
  totalFromDenominations,
  buildZReport,
};

export type { TryDenominationCode };
